/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IPersonaProps, IPersonaStyles, Persona, PersonaSize } from "@fluentui/react";
import { observer } from "mobx-react";
import * as React from "react";

import { LoadingState, UsersPicturesStore, loadUserPicture } from "../../../sh-stores/sh-users-pictures-store";

import { IUserPersonaProps } from "./IUserPersonaProps";
import { updateTextProps } from "./UserPersona.mappers";
import { useCrossLocationTeamName } from "../shift/shiftDetails/CrossLocationTeamName.mappers";

/**
 * Shows user persona with picture.
 * @param props The user persona properties.
 * @returns The JSX element.
 */
export const UserPersonaWithPicture: React.FC<IUserPersonaProps> = observer(props => {
    const { userId, imageUrl, crossLocationTeamId, ...remainingProps } = props;
    const personaStyles: Partial<IPersonaStyles> = { secondaryText: { minHeight: '1rem', height: 'auto' } };

    // TODO (CLM): Consider moving to mapper.
    const hasUserPicture = userId && UsersPicturesStore().pictureUrls.has(userId);
    const loadingState = UsersPicturesStore().loadingStates.get(userId) ?? LoadingState.NotStarted;
    const imgUrl = hasUserPicture ? UsersPicturesStore().pictureUrls.get(userId) : imageUrl;

    // Load user picture if userId provided and not already loaded.
    if (userId && loadingState == LoadingState.NotStarted) {
        loadUserPicture(userId);
    }

    // Get team name for Cross Location shifts and update persona text props if needed.
    let teamName = useCrossLocationTeamName({ crossLocationTeamId });
    const personaProps = updateTextProps(remainingProps, teamName);

    // Custom render if tertiaryText is provided.
    // This is needed as Persona will only render primary and secondary text if iconSize is too small.
    if (personaProps.tertiaryText && personaProps.size < PersonaSize.size72) {
        personaProps.onRenderSecondaryText = _onRenderText;
    }
    // Unable to use class names for secondary text so using styles as suggested in the Fluent 8 documentation
    // Check 'Rendering custom person text' https://developer.microsoft.com/en-us/fluentui#/controls/web/persona
    return <Persona {...personaProps} imageUrl={imgUrl} styles={personaStyles} />;
});

/**
 * Renders the second and third line of text (tertiaryText).
 * @param props The persona properties.
 * @returns The JSX element.
 */
function _onRenderText(props: IPersonaProps): JSX.Element {
    const { secondaryText, tertiaryText } = props;

    return (
        <>
            {secondaryText && <div>{secondaryText}</div>}
            {tertiaryText && <div>{tertiaryText}</div>}
        </>
    );
}